/**
 * A very small element builder, and the only way screens are built.
 *
 * The alternative is templating strings into `innerHTML`, which was fine while
 * every word on screen was written in this repository. List and item content is
 * not: it is whatever the person — or their agent — typed, redrawn on every
 * change. Escaping that correctly every single time is a discipline, and
 * disciplines lapse; assigning `textContent` makes the escaping unnecessary
 * rather than merely careful.
 */

type AttrValue = string | number | boolean | null | undefined;

export interface ElOptions {
  class?: string;
  text?: string;
  attrs?: Record<string, AttrValue>;
  on?: Record<string, (event: Event) => void>;
  children?: (Node | string | false | undefined)[];
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElOptions = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (options.class !== undefined) node.className = options.class;
  if (options.text !== undefined) node.textContent = options.text;

  for (const [name, value] of Object.entries(options.attrs ?? {})) {
    // `false`, `null` and `undefined` all mean "absent". Stringifying them
    // would produce `disabled="false"`, which disables the control.
    //
    // ARIA attributes are the exception and do not want this: they are
    // string-valued, so `aria-pressed={false}` drops the attribute and the
    // element stops being a toggle rather than being an unpressed one. Pass
    // `String(...)` for those.
    if (value === false || value === null || value === undefined) continue;
    node.setAttribute(name, value === true ? "" : String(value));
  }

  for (const [type, listener] of Object.entries(options.on ?? {})) {
    node.addEventListener(type, listener);
  }

  for (const child of options.children ?? []) {
    if (child === false || child === undefined) continue;
    node.append(child);
  }

  return node;
}

/** Conditional children read better as a value than as a branch in a list. */
export function when(condition: boolean, build: () => Node): Node | false {
  return condition ? build() : false;
}

/**
 * Writes text only when it differs. A repaint that rewrites the same words
 * replaces the text node, and a selection someone had made in it goes with
 * it: a delta for another field of the row, arriving while they read or copy
 * the title, cleared what they had selected.
 */
export function setText(node: Node, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

/**
 * Moves a node already on the page, keeping the focus and a field's
 * selection inside it.
 *
 * Moving a node the usual way takes it out and puts it back, and taking it
 * out blurs whatever inside it had the focus: a row moved by another device
 * while its title was being edited lost the caret and the selected text, and
 * the next thing typed went nowhere. `moveBefore` moves without removing
 * where the browser has it; elsewhere, what had the focus gets it back, with
 * the selection it held.
 */
export function moveKeepingFocus(parent: Node, node: Element, before: Node | null): void {
  const move = (parent as Node & { moveBefore?(node: Node, child: Node | null): void }).moveBefore;
  if (typeof move === "function" && node.isConnected && parent.isConnected) {
    try {
      move.call(parent, node, before);
      return;
    } catch {
      // A move the browser will not make atomically: the ordinary way below.
    }
  }
  const focused = document.activeElement;
  const held = focused instanceof HTMLElement && node.contains(focused) ? focused : undefined;
  const field = held instanceof HTMLTextAreaElement || held instanceof HTMLInputElement ? held : undefined;
  const range = field ? { start: field.selectionStart, end: field.selectionEnd, direction: field.selectionDirection } : undefined;
  parent.insertBefore(node, before);
  if (!held?.isConnected) return;
  held.focus({ preventScroll: true });
  if (field && range?.start != null && range.end != null) field.setSelectionRange(range.start, range.end, range.direction ?? undefined);
}
